import { lazy, Suspense, useEffect, useState } from 'react';

import { adminEntities, spenderEntityIds } from './admin/perms.ts';
import { api, isDemo } from './api/index.ts';
import type { Me } from './api/types.ts';
import { LocationNotice, Login } from './auth/Login.tsx';
import { LocationGate } from './spender/LocationGate.tsx';
import { setLang, setPlaceWords } from './i18n/index.ts';
import { wordsOf } from './lib/terms.ts';
import { WordsProvider } from './lib/words.tsx';
import { WALLET_ALT_HOME, navigate, setWalletHome, useRoute } from './lib/router.ts';
import { AddExpense } from './spender/AddExpense.tsx';
import { GiveCash } from './spender/GiveCash.tsx';
import { History } from './spender/History.tsx';
import { Home } from './spender/Home.tsx';
import { NoteSyncProvider } from './spender/noteSync.tsx';
import { SpenderProvider } from './spender/store.tsx';
import { PageLoader } from './ui/parts.tsx';
import { StatusPage } from './ui/StatusPage.tsx';

// The admin app (and its chart library) downloads only when an owner opens it, so office
// boys on weak market signal never pay for it.
const AdminApp = lazy(() => import('./admin/AdminApp.tsx').then((m) => ({ default: m.AdminApp })));

type Session = { state: 'loading' } | { state: 'out' } | { state: 'in'; me: Me } | { state: 'error' };

export function App() {
  const route = useRoute();
  const [session, setSession] = useState<Session>({ state: 'loading' });

  const load = () => {
    setSession({ state: 'loading' });
    api
      .me()
      .then((me) => {
        if (me) setLang(me.language);
        setSession(me ? { state: 'in', me } : { state: 'out' });
      })
      .catch(() => setSession({ state: 'error' }));
  };
  useEffect(load, []);

  if (session.state === 'loading') return <PageLoader />;
  if (session.state === 'error') {
    // No internet is the likeliest cause in the market; otherwise it's the server.
    return <StatusPage kind={navigator.onLine ? 'server' : 'offline'} onRetry={load} />;
  }
  if (session.state === 'out') {
    return (
      <Login
        onSignedIn={(me) => {
          setLang(me.language);
          setSession({ state: 'in', me });
          navigate('/', { replace: true });
        }}
      />
    );
  }

  const me = session.me;
  setPlaceWords(wordsOf(me));
  // After a change to the person's own access (e.g. a company added): fetch it again quietly.
  const refreshMe = async () => {
    const fresh = await api.me();
    if (fresh) setSession({ state: 'in', me: fresh });
  };
  const logout = () => {
    void api.logout().then(() => setSession({ state: 'out' }));
  };
  // Office boys and roles with "Records own purchases" use the phone app; Owners and every
  // other role (Viewer, Accountant, …) the owner app. Someone who is both lands on the
  // dashboard; their wallet app lives at /wallet (each app links to the other).
  const isSpender = spenderEntityIds(me).size > 0;
  const isAdmin = me.isSuperAdmin || adminEntities(me).length > 0;
  const walletHome = isAdmin ? WALLET_ALT_HOME : '/';
  setWalletHome(walletHome);
  if (isAdmin && (!isSpender || route.path === '/' || route.path.startsWith('/admin'))) {
    if (route.path === '/') navigate('/admin', { replace: true });
    else if (!route.path.startsWith('/admin')) return <StatusPage kind="notFound" homePath="/admin" />;
    return (
      <Suspense fallback={<PageLoader />}>
        <WordsProvider words={wordsOf(me)}>
          <AdminApp me={me} onLogout={logout} refreshMe={refreshMe} />
        </WordsProvider>
      </Suspense>
    );
  }
  if (!isSpender) return <StatusPage kind="notFound" />;

  let screen;
  if (route.path === walletHome) screen = <Home onLogout={logout} ownerApp={isAdmin} />;
  else if (route.path === '/add') screen = <AddExpense key={route.query.get('fix') ?? 'new'} />;
  else if (route.path === '/history') screen = <History />;
  else if (route.path === '/give') screen = <GiveCash />;
  else return <StatusPage kind="notFound" />;

  return (
    // phone-app: no visible scrollbars anywhere in the office boys' app.
    <div className="phone-app contents">
    <LocationNotice userId={me.id}>
      {/* A usable location before Home (owner's decision, 10 Oct 2026). */}
      <LocationGate>
      <SpenderProvider me={me}>
        <NoteSyncProvider>
          {isDemo && <DemoBadge />}
          {screen}
        </NoteSyncProvider>
      </SpenderProvider>
      </LocationGate>
    </LocationNotice>
    </div>
  );
}

function DemoBadge() {
  return (
    <div className="pointer-events-none fixed top-1 left-1/2 z-50 -translate-x-1/2 rounded bg-warning px-2 py-0.5 text-[11px] font-bold tracking-wide text-white uppercase">
      Demo data
    </div>
  );
}
